import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Evidence } from '@/components/extraction/Evidence';
import { formatCalendarDate, formatCurrency } from '@/utils/formatters';
import { costCurrency, createsCostRecord, displayValue, invoiceTarget, type ReviewSelection } from '@/utils/extractionReview';
import type { ExtractionDetail, InvoiceContractCandidate } from '@/types';

/*
 * The invoice half of the review page (#25, spec 6.0.2). Everything from the document (vendor name,
 * descriptions, matched terms) is rendered as plain text, like every other model value: the words
 * were written by whoever wrote the invoice.
 */

interface CardProps {
  detail: ExtractionDetail;
  selection: ReviewSelection;
  setSelection: (s: ReviewSelection) => void;
}

const money = (amount: number, currency?: string) => {
  try {
    return formatCurrency(amount, { currency: currency?.trim() || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch {
    return String(amount);
  }
};

const proposalSummary = (detail: ExtractionDetail): string => {
  const proposal = detail.invoice?.contractProposal;
  const proposed = proposal?.candidates.find((c) => c.id === proposal.proposedContractId);
  switch (proposal?.kind) {
    case 'other':
      return `The line items point to ${proposed?.contractNumber}, not the contract this document is on.`;
    case 'current':
      return 'The line items point to the contract this document is on.';
    case 'ambiguous':
      return 'The line items fit several contracts equally: pick one, or keep it where it is.';
    default:
      return 'The line items name no contract of this vendor. Check them and pick one, or keep it where it is.';
  }
};

const CandidateText: React.FC<{ candidate: InvoiceContractCandidate; current: boolean; proposed: boolean }> = ({ candidate, current, proposed }) => (
  <span className="min-w-0">
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="font-medium text-grey-900 break-words">
        {candidate.contractNumber} · {candidate.title}
      </span>
      {current && <Badge variant="outline">Current</Badge>}
      {proposed && <Badge variant="info">Proposed</Badge>}
      {candidate.hasDuplicate && <Badge variant="warning">Already recorded here</Badge>}
    </span>
    <span className="block text-xs text-grey-500 break-words">
      {[candidate.vendorName, candidate.status, candidate.currency].filter(Boolean).join(' · ')}
    </span>
    {candidate.matchedTerms.length > 0 && (
      <span className="block text-xs text-grey-600 break-words">Found in the lines: {candidate.matchedTerms.join(', ')}</span>
    )}
  </span>
);

/** Where the document belongs: the proposal, why, and the reviewer's choice. Nothing is chosen for them. */
export const InvoiceContractCard: React.FC<CardProps & { editable: boolean }> = ({ detail, selection, setSelection, editable }) => {
  const invoice = detail.invoice;
  const proposal = invoice?.contractProposal;
  const vendorRow = detail.fields.find((f) => f.name === 'vendorName' && f.itemIndex === undefined);

  if (!invoice) return null;
  if (!proposal) {
    return invoice.movedFromContractId ? (
      <Card data-testid="invoice-contract">
        <CardHeader>
          <CardTitle>Contract</CardTitle>
          <CardDescription>
            Moved here from {invoice.movedFromContractNumber ?? 'another contract'} by {detail.reviewedBy}.
          </CardDescription>
        </CardHeader>
      </Card>
    ) : null;
  }

  return (
    <Card data-testid="invoice-contract">
      <CardHeader>
        <CardTitle>Contract</CardTitle>
        <CardDescription data-testid="invoice-proposal-summary">{proposalSummary(detail)}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {vendorRow?.found && (
          <div>
            <p className="text-xs text-grey-500">The invoice names</p>
            <p className="text-grey-900 font-medium break-words">{vendorRow.value}</p>
            <Evidence quote={vendorRow.citedText} location={vendorRow.location} verified={vendorRow.quoteVerified} className="mt-1" />
          </div>
        )}
        <fieldset className="space-y-2" aria-label="Contract for this invoice">
          {proposal.candidates.map((candidate) => {
            const current = candidate.id === proposal.currentContractId;
            const proposed = candidate.id === proposal.proposedContractId;
            const content = <CandidateText candidate={candidate} current={current} proposed={proposed} />;
            return editable && proposal.candidates.length > 1 ? (
              <label key={candidate.id} className="flex items-start gap-2 rounded-md border border-border p-2" data-testid={`candidate-${candidate.contractNumber}`}>
                <input
                  type="radio"
                  name="invoice-contract"
                  className="mt-1"
                  checked={current ? !selection.moveTo : selection.moveTo === candidate.id}
                  onChange={() => setSelection({ ...selection, moveTo: current ? undefined : candidate.id })}
                  aria-label={current ? `Keep on ${candidate.contractNumber}` : `Move to ${candidate.contractNumber}`}
                />
                {content}
              </label>
            ) : (
              <div key={candidate.id} className="rounded-md border border-border p-2" data-testid={`candidate-${candidate.contractNumber}`}>
                {content}
              </div>
            );
          })}
        </fieldset>
      </CardContent>
    </Card>
  );
};

/** The lines as printed: context for the proposal, never written */
export const InvoiceLinesCard: React.FC<{ detail: ExtractionDetail }> = ({ detail }) => {
  const lines = detail.invoice?.lines ?? [];
  if (lines.length === 0) return null;
  const currency = detail.extractedCurrency ?? detail.contractCurrency;
  return (
    <Card data-testid="invoice-lines">
      <CardHeader>
        <CardTitle>Invoice lines</CardTitle>
        <CardDescription>Shown for context; they are not written anywhere.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border text-sm">
          {lines.map((line) => (
            <li key={line.itemIndex} className="py-2 first:pt-0 last:pb-0">
              <div className="flex flex-wrap justify-between gap-x-4">
                <span className="min-w-0 break-words text-grey-900">{line.description ?? 'No description'}</span>
                <span className="text-grey-800">{displayValue('amount', line.amount, currency)}</span>
              </div>
              <p className="text-xs text-grey-500">
                {[line.quantity && `Qty ${line.quantity}`, line.unitPrice && `at ${displayValue('amount', line.unitPrice, currency)}`]
                  .filter(Boolean)
                  .join(' ')}
              </p>
              <p className="text-xs text-grey-500">
                {line.quoteVerified
                  ? `Found on ${line.location ?? 'the page'}`
                  : `Not found in the document text${line.location ? ` (${line.location})` : ''}`}
              </p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const Row: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div>
    <dt className="text-xs text-grey-500">{label}</dt>
    <dd className="text-grey-800 break-words">{value || '—'}</dd>
  </div>
);

/** What the apply will create from the current selection, and what it created */
export const CostRecordCard: React.FC<{ detail: ExtractionDetail; selection: ReviewSelection }> = ({ detail, selection }) => {
  const applied = detail.invoice?.appliedCostRecord;
  if (applied) {
    return (
      <Card data-testid="cost-record-created">
        <CardHeader>
          <CardTitle>Cost record created</CardTitle>
          <CardDescription className="break-words">{applied.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Row label="Type" value={applied.recordType} />
            <Row label="Amount" value={money(applied.amount, applied.currency)} />
            <Row label="Date" value={formatCalendarDate(applied.date)} />
            <Row label="Invoice number" value={applied.invoiceNumber ?? undefined} />
          </dl>
        </CardContent>
      </Card>
    );
  }

  if (detail.status !== 'Review' || !createsCostRecord(detail, selection)) return null;

  const value = (name: string) => {
    const row = detail.fields.find((f) => f.group === 'invoice' && f.itemIndex === undefined && f.name === name);
    if (!row || !selection.fields[row.id]?.accepted) return undefined;
    return selection.fields[row.id].edited?.trim() || row.value;
  };
  const amount = Number(value('amount'));
  const currency = costCurrency(detail, selection);
  const target = invoiceTarget(detail, selection);
  return (
    <Card data-testid="cost-record-preview">
      <CardHeader>
        <CardTitle>Cost record to create</CardTitle>
        <CardDescription>On {target?.contractNumber ?? 'this contract'}, from the values selected above.</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Row label="Type" value={amount < 0 ? 'Credit' : 'Invoice'} />
          <Row label="Amount" value={Number.isFinite(amount) ? money(Math.abs(amount), currency) : undefined} />
          <Row label="Date" value={displayValue('invoiceDate', value('invoiceDate'))} />
          <Row label="Invoice number" value={value('invoiceNumber')} />
        </dl>
      </CardContent>
    </Card>
  );
};
